import { AuthApiErrorCode } from '@mfw/common';

import BaseApiResponseError from './BaseApiResponseError';

/**
 * The boot renewal is terminal — `renewCookie` sends `_retry: true` and
 * `skipSessionRenewal: true`, so its own refusal is never replayed. When THAT
 * request is refused for the IDENTITY — the guard will not bind the token's
 * tenant to an account here, or the account is archived or deleted — restoring
 * the persisted Firebase identity on the next boot only repeats the refusal, so
 * it is dropped. Every code this predicate names — the three backend ones below
 * and the two Firebase ones — is in `isSessionOverError` too, which is the same
 * predicate plus a 401: terminal wherever they arrive.
 *
 * The single chokepoint for that question: `AuthProvider` signs out on it, and
 * `reportHardSessionFailure` demotes its own report and withholds the on-screen message on
 * it (MYFITWORLD-V2-6Q/-6R). Lives in `api/http` beside `isConnectivityError`, and for the
 * same two reasons: neither caller pulls in the other's module, and one definition is what
 * keeps the two layers from ever answering differently about one code (sen-3 M1).
 *
 * `INVALID_TOKEN` is deliberately NOT here (sen-6 S1). It is the guard's answer
 * to EVERY verification failure, and the Admin SDK folds a signing-key fetch
 * failure into the same `auth/argument-error` as a bad signature — so on a boot,
 * where the token was just minted by the Firebase client, that code is far more
 * often the backend's key cache or clock than the identity. Signing out on it
 * turned one unreachable `www.googleapis.com` into a forced sign-in for every
 * user booting in that window. The stale-tenant sessions that once motivated it
 * (den-5 §2) are answered `TENANT_MISMATCH` now that the verifier trusts the
 * tenant the token claims, and so is a token with no tenant claim at all.
 *
 * PR-2: `SESSION_EXPIRED` joins for the renewal path — the mint presented a token that is
 * itself pre-cutoff; and the two Firebase codes on which the SDK signs out locally, so the
 * guard names them terminal rather than an outage.
 */
const FIREBASE_TERMINAL_CODES: readonly string[] = [
  'auth/user-token-expired',
  'auth/user-disabled',
];

export function isRefusedRenewalError(error: unknown) {
  if (error instanceof BaseApiResponseError) {
    return (
      error.code === AuthApiErrorCode.TENANT_MISMATCH ||
      error.code === AuthApiErrorCode.ACCOUNT_SUSPENDED ||
      error.code === AuthApiErrorCode.SESSION_EXPIRED
    );
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' && FIREBASE_TERMINAL_CODES.includes(code);
  }
  return false;
}
